import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LuMenu, LuX, LuPhone, LuMail } from 'react-icons/lu';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Technologies', path: '/technologies' },
  { name: 'Blog', path: '/blog' },
  { name: 'Careers', path: '/careers' }
];

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <>
      {/* Top Bar - Hidden on mobile */}
      <div className="hidden lg:block bg-slate-900 border-b border-slate-800/50 py-2">
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-6 text-xs text-slate-400">
            <a href="mailto:admin@techmeetsu.com" className="flex items-center gap-2 hover:text-orange-400 transition-colors">
              <LuMail size={14} />
              admin@techmeetsu.com
            </a>
            <a href="tel:+919152929786" className="flex items-center gap-2 hover:text-orange-400 transition-colors">
              <LuPhone size={14} />
              +91 91529 29786
            </a>
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <span>Your Complete Technology & Digital Growth Partner</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 lg:top-10 left-0 right-0 z-50 transition-all duration-300 ${scrolled
          ? 'lg:top-0 bg-slate-950/98 backdrop-blur-xl shadow-2xl shadow-black/20'
          : 'bg-slate-950/95 backdrop-blur-lg'
          }`}
        data-testid="main-navigation"
      >
        {/* Gradient line at top */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-blue-500 via-orange-500 to-blue-500" />

        <div className="container mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
              <motion.img
                src="https://customer-assets.emergentagent.com/job_innovate-tech-4/artifacts/656jawr7_withoutBg.png"
                alt="TechMeetsU"
                className="h-10 w-auto lg:h-12"
                whileHover={{ scale: 1.05 }}
              />
              <div className="block">
                <div className="text-white font-heading font-bold text-lg leading-tight group-hover:text-orange-400 transition-colors">
                  TechMeetsU
                </div>
                <div className="text-[10px] text-slate-400 uppercase tracking-wider">
                  Where Tech Meets You
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center">
              {/* Nav Links with separator dots */}
              <div className="flex items-center bg-slate-900/50 rounded-full px-2 py-1 border border-slate-800/50">
                {navLinks.map((link, index) => (
                  <div key={link.path} className="flex items-center">
                    <Link
                      to={link.path}
                      className={`relative px-4 py-2 text-sm font-medium transition-all rounded-full ${location.pathname === link.path
                        ? 'text-white bg-gradient-to-r from-blue-600/20 to-orange-600/20'
                        : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                        }`}
                      data-testid={`nav-link-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                      {location.pathname === link.path && (
                        <motion.div
                          layoutId="activeNav"

                        />
                      )}
                    </Link>
                    {index < navLinks.length - 1 && (
                      <span className="w-1 h-1 rounded-full bg-slate-700 mx-1" />
                    )}
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Link
                to="/contact"
                className="ml-6 relative group"
                data-testid="nav-cta-button"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-orange-500 rounded-full blur-md opacity-60 group-hover:opacity-100 transition-opacity" />
                <div className="relative bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 text-white px-6 py-2.5 rounded-full text-sm font-bold transition-all flex items-center gap-2">
                  <span>Get Started</span>
                  <motion.span
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    →
                  </motion.span>
                </div>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden relative z-50 text-white p-2.5 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-orange-500/50 transition-all"
              data-testid="mobile-menu-button"
            >
              <motion.div
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {isOpen ? <LuX size={22} /> : <LuMenu size={22} />}
              </motion.div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-slate-950/98 backdrop-blur-xl border-t border-slate-800/50"
              data-testid="mobile-menu"
            >
              <div className="container mx-auto px-4 py-6">
                {/* Mobile Contact Info */}
                <div className="flex flex-col gap-2 mb-6 pb-6 border-b border-slate-800/50">
                  <a href="mailto:admin@techmeetsu.com" className="flex items-center gap-2 text-sm text-slate-400 hover:text-orange-400">
                    <LuMail size={16} />
                    admin@techmeetsu.com
                  </a>
                  <a href="tel:+919152929786" className="flex items-center gap-2 text-sm text-slate-400 hover:text-orange-400">
                    <LuPhone size={16} />
                    +91 91529 29786
                  </a>
                </div>

                {/* Mobile Nav Links */}
                <div className="space-y-1">
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.path}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={link.path}
                        className={`flex items-center gap-3 py-3 px-4 text-base font-medium rounded-xl transition-all ${location.pathname === link.path
                          ? 'text-orange-400 bg-orange-500/10 border-l-4 border-orange-500'
                          : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                          }`}
                        data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${location.pathname === link.path ? 'bg-orange-500' : 'bg-slate-600'
                          }`} />
                        {link.name}
                      </Link>
                    </motion.div>
                  ))}
                </div>

                {/* Mobile CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mt-6"
                >
                  <Link
                    to="/contact"
                    className="block text-center bg-gradient-to-r from-orange-600 to-orange-500 text-white px-6 py-4 rounded-2xl text-base font-bold shadow-lg shadow-orange-500/30"
                    data-testid="mobile-nav-cta-button"
                  >
                    Get Started →
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
};
